import type { SourceId, VizSourceEntity } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { useSelector } from '../../../../Flux'
import type { State } from '../../../../Flux/Slice'
import { Actions } from '../../../../Flux/Slice'
import { useDispatch } from 'react-redux'
import React, { useEffect, useState } from 'react'
import { DateTime } from 'luxon'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.ts'
import { trackVizMode } from '../../../../Services/Telemetry/index.ts'
import { useGetAuthenticatedUser } from '../../../../Flux/Selector'
import { canChooseSpinAlgorithm, canUseSpin, canUseSpinOnOrder } from '../../../../Model'
import { PREFERRED_SPIN_ALGORITHM, resolveSpinAlgorithm, visibleSpinAlgorithms } from '../../../../Model/Spin/spinAlgorithm.ts'
import { describeSpinStatus, spinEmptyStateText } from '../../../../Model/Spin/spinStatus.ts'
import { SpinPendingBadge, useSpinScopeStatus } from '../../SpinStatusLabel.tsx'
import type { SpinChangePoint } from '../../../../Repository/Visualization/SpinMeta.ts'
import { useSpinAlgorithms, useSpinChangePoints, useSpinUnspinnedCount } from '../../useSpinMeta.ts'

const LATEST = '' as const

const formatAsOf = (cp: SpinChangePoint): string => {
  const when = DateTime.fromISO(cp.assignedAt).toFormat('yyyy-LL-dd HH:mm:ss')
  return `${when} · ${cp.changedOtus} changed`
}

/**
 * "Use SPIN clustering" source knob — the SPIN sibling of {@link NatureKPIPanel}.
 *
 * When enabled, the source is sourced from the `spin.*` cube (identity clustering) instead of the
 * plain OTU cube, adding two server-affecting knobs: the clustering **algorithm** and a
 * **point-in-time** (`atTime`, latest-valid SPIN per OTU as of that moment). Everything downstream
 * is unchanged — the SPIN cube wires the same Arrow shape.
 *
 * Both pickers are data-driven so they can never offer a selection that returns nothing:
 * - the algorithm list comes from the scope's SPIN state (only algorithms with coverage FOR THIS SCOPE); an
 *   empty list means the scope has no SPIN data and the knob says so.
 * - the "as of" list comes from the same state's change-points (the real timestamps where the scope's SPINs
 *   changed) plus a "Latest" default — no free-form date entry to get wrong.
 *
 * INSIGHT-gated like the scope itself: on an order, own or lab-shared licence; on a project, own.
 * Without `canChooseSpinAlgorithm` only the preferred algorithm is offered, and it is preselected.
 */
export default function SpinPanel({ sourceId }: { sourceId: SourceId }) {
  const dispatch = useDispatch()
  const user = useGetAuthenticatedUser()
  const src: VizSourceEntity | null = useSelector((s: typeof State) => s.visualization.sources[sourceId] ?? null)
  const scope = useSelector((s: typeof State) => s.visualization.scope)
  const scopeId = scope?.id ?? null
  const order = useSelector((s: typeof State) => (s.order?.order === 'LOADING' ? undefined : s.order?.order))

  const applied = src?.shared?.spin
  const appliedAlgorithm = applied?.algorithmType ?? ''
  const appliedAtTime = applied?.atTime ?? LATEST

  const [editorEnabled, setEditorEnabled] = useState(false)
  const [draftAlgorithm, setDraftAlgorithm] = useState<string | undefined>(undefined)
  const [draftAtTime, setDraftAtTime] = useState<string | undefined>(undefined)

  // The order workspace loads its order into redux; one left over from another order must not count.
  const spinAccess =
    scope?.kind === 'order' ? canUseSpinOnOrder(order?.id === scopeId ? order : null, user) : canUseSpin(user)
  const algorithmChoice = canChooseSpinAlgorithm(user)
  const showEditor = applied !== undefined || editorEnabled
  const statusScope = scope?.kind === 'order' ? 'ORDER' : 'PROJECT'
  const spinStatus = useSpinScopeStatus(scopeId ?? undefined, spinAccess && showEditor)
  const spinStatusDescription = describeSpinStatus(spinStatus, statusScope)
  // Loaded once the editor is open. `null` = not loaded yet; `[]` = scope has no SPIN data. `algosError` distinguishes
  // a failed fetch from a genuinely empty scope.
  const { algorithms, error: algosError } = useSpinAlgorithms(scopeId, spinAccess && showEditor)

  // Keep an applied selection visible even if it isn't in the freshly-loaded lists.
  const algorithmOptions = !algorithms
    ? null
    : !algorithmChoice
      ? visibleSpinAlgorithms(algorithms, false)
      : appliedAlgorithm && !algorithms.includes(appliedAlgorithm)
        ? [appliedAlgorithm, ...algorithms]
        : algorithms

  const selectedAlgorithm = resolveSpinAlgorithm(draftAlgorithm ?? appliedAlgorithm, algorithmOptions, algorithmChoice)
  const selectedAtTime = draftAtTime ?? appliedAtTime
  const changePoints = useSpinChangePoints(scopeId, selectedAlgorithm, spinAccess && showEditor)
  // How much data the SPIN view would drop under the selected algorithm and "as of".
  const unspind = useSpinUnspinnedCount(scopeId, selectedAlgorithm, selectedAtTime, spinAccess && showEditor)

  // A source can arrive with another algorithm applied (e.g. a chart the chat agent built on request). Without the
  // choice the panel can only show the preferred one, so move the source onto it — or off SPIN if the scope lacks it.
  useEffect(() => {
    if (!spinAccess || algorithmChoice || !applied || appliedAlgorithm === PREFERRED_SPIN_ALGORITHM || algorithms === null) return
    dispatch(
      Actions[visualizationSlice.name].updateSourceShared({
        sourceId,
        patch: {
          spin: algorithms.includes(PREFERRED_SPIN_ALGORITHM) ? { algorithmType: PREFERRED_SPIN_ALGORITHM, atTime: LATEST } : undefined
        }
      })
    )
  }, [spinAccess, algorithmChoice, applied, appliedAlgorithm, algorithms, dispatch, sourceId])

  if (!spinAccess) return null

  const asOfKnown = new Set(changePoints.map((cp) => cp.assignedAt))
  const orphanApplied = selectedAtTime !== LATEST && !asOfKnown.has(selectedAtTime) ? selectedAtTime : undefined

  const canApply =
    selectedAlgorithm !== '' && (selectedAlgorithm !== appliedAlgorithm || selectedAtTime !== appliedAtTime)

  const apply = () => {
    if (!canApply) return
    trackVizMode('spin', 'apply', { algorithm: selectedAlgorithm, at_time: selectedAtTime || 'latest' })
    dispatch(
      Actions[visualizationSlice.name].updateSourceShared({
        sourceId,
        patch: { spin: { algorithmType: selectedAlgorithm, atTime: selectedAtTime } }
      })
    )
    setDraftAlgorithm(undefined)
    setDraftAtTime(undefined)
  }

  const disable = () => {
    trackVizMode('spin', 'disable')
    dispatch(Actions[visualizationSlice.name].updateSourceShared({ sourceId, patch: { spin: undefined } }))
    setDraftAlgorithm(undefined)
    setDraftAtTime(undefined)
    setEditorEnabled(false)
  }

  const noSpinData = algorithmOptions !== null && algorithmOptions.length === 0

  return (
    <div className={'flex flex-col justify-start gap-2'}>
      <div className={'flex items-center gap-2 font-semibold'}>
        <label className="font-semibold">Use SPIN clustering</label>
        <input
          type="checkbox"
          data-testid="spin-toggle"
          className="toggle toggle-success"
          checked={showEditor}
          onChange={() => {
            if (showEditor) {
              disable()
            } else {
              trackVizMode('spin', 'enable')
              setEditorEnabled(true)
              setDraftAlgorithm(undefined)
              setDraftAtTime(undefined)
            }
          }}
        />
      </div>

      {showEditor && algosError && (
        <span className="text-xs text-red-500">Couldn’t load SPIN algorithms — is the backend up to date?</span>
      )}

      {showEditor && !algosError && algorithms === null && (
        <span className="text-xs opacity-70">Loading SPIN algorithms…</span>
      )}

      {showEditor && !algosError && noSpinData && (
        <span data-testid="spin-no-data" className="text-xs text-gray-500">
          {spinEmptyStateText(spinStatus, statusScope)}
        </span>
      )}

      {showEditor && !noSpinData && algorithms !== null && <SpinPendingBadge description={spinStatusDescription} showDetail />}

      {showEditor && !algosError && !noSpinData && algorithms !== null && (
        <div className={'flex flex-col gap-2'}>
          <div className="flex flex-col">
            <label className="font-semibold">Algorithm</label>
            {algorithmChoice ? (
              <select
                data-testid="spin-algorithm"
                className="select select-bordered select-sm"
                value={selectedAlgorithm}
                onChange={(e) => setDraftAlgorithm(e.target.value)}
              >
                <option key="__placeholder" value="" disabled>
                  Select an algorithm
                </option>
                {(algorithmOptions ?? []).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            ) : (
              <span data-testid="spin-algorithm-label">{selectedAlgorithm}</span>
            )}
          </div>

          <div className="flex flex-col">
            <label className="font-semibold">As of</label>
            <span className="text-xs opacity-70">Latest valid SPIN per OTU as of the chosen point in time.</span>
            <select
              className="select select-bordered select-sm"
              value={selectedAtTime}
              onChange={(e) => setDraftAtTime(e.target.value)}
              disabled={selectedAlgorithm === ''}
            >
              <option value={LATEST}>Latest</option>
              {orphanApplied && (
                <option key={orphanApplied} value={orphanApplied}>
                  {DateTime.fromISO(orphanApplied).toFormat('yyyy-LL-dd HH:mm:ss')}
                </option>
              )}
              {changePoints.map((cp) => (
                <option key={cp.assignedAt} value={cp.assignedAt}>
                  {formatAsOf(cp)}
                </option>
              ))}
            </select>
          </div>

          {unspind &&
            unspind.totalOtus > 0 &&
            (unspind.unspinnedOtus === 0 ? (
              <span className="text-xs text-gray-500">
                All {unspind.totalOtus.toLocaleString()} OTUs in scope are clustered under {selectedAlgorithm}.
              </span>
            ) : (
              <span className="text-xs text-warning">
                {unspind.unspinnedOtus.toLocaleString()} of {unspind.totalOtus.toLocaleString()} OTUs (
                {Math.round((unspind.unspinnedOtus / unspind.totalOtus) * 100)}%) have no {selectedAlgorithm} SPIN
                {selectedAtTime === LATEST ? '' : ' as of the chosen time'} and are excluded from this view.
              </span>
            ))}

          <button data-testid="spin-apply" className="btn btn-sm btn-primary" onClick={apply} disabled={!canApply}>
            Apply
          </button>

          <span className={'text-xs italic'}>
            SPIN groups OTUs into stable identities across orders; the same identity is deduplicated and its reads
            summed within each sample.
          </span>
        </div>
      )}
    </div>
  )
}
